import {ChangeDetectionStrategy, Component, computed, DestroyRef, Injectable, input, OnInit, signal} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {GruppeId, GruppeInvitasjonSpesifikasjoner, GruppeMedlemListeModell, GruppeModell, MedlemsRolleModell, MedlemsRollePeriode2Modell, OpprettGruppeInvitasjonSpesifikasjon} from "../../../../../../felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {TranslateModule} from "@ngx-translate/core";
import {Observable, OperatorFunction} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MedlemImporterSkjemalinjeKomponent, MedlemImporterSkjemalinjeType, MedlemImporterSkjemalinjeVerdier} from "../medlem-importer-skjemalinje/medlem-importer-skjemalinje";
import {erLikIgnorerCasing, trimTilNull} from "felles-ng2/støttefunksjoner/strenger";
import {VelgGruppeKomponent} from "../../../../gruppe/komponenter/velg-gruppe/velg-gruppe";
import {MedlemImporterSkjemaoverskrifterKomponent} from "web-ng2/moduler/medlem/importer-medlemmer/komponenter/medlem-importer-skjemaoverskrifter/medlem-importer-skjemaoverskrifter";
import {GjeldendeTidssone} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {Temporal} from "temporal-polyfill";
import {MedlemImporterSkjema} from "web-ng2/moduler/medlem/importer-medlemmer/modale/medlem-importer-dialog/medlem-importer-dialog";
import {map} from "rxjs/operators";
import {medlemmerMedTilgangMedBrukertilknytning, medlemmerMedTilgangUtenBrukertilknytning, medlemmerUtenTilgangMedBrukertilknytning, primærMedlemsrolle} from "felles-ng2/modell/medlemModellHjelpere";
import {lesPlainDateSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import {GruppeDatatjeneste} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import PlainDate = Temporal.PlainDate;

const debug = initDebugger('MedlemImporterFraGruppeKomponent');


@Component({
    selector: 'medlem-importer-fra-gruppe',
    templateUrl: './medlem-importer-fra-gruppe.html',
    styleUrl: './medlem-importer-fra-gruppe.scss',
    imports: [
        MatLabel,
        MatFormField,
        TranslateModule,
        VelgGruppeKomponent,
        MedlemImporterSkjemaoverskrifterKomponent,
        MedlemImporterSkjemalinjeKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedlemImporterFraGruppeKomponent implements MedlemImporterSkjema, OnInit {

    gruppeId = input.required<GruppeId>();
    medlemsroller = input.required<MedlemsRolleModell[]>();

    valgtGruppe = new FormControl<GruppeModell | null>(null);
    skjema = new FormGroup({
        medlemmer: new FormArray<MedlemImporterSkjemalinjeType>([]),
        invitasjoner: new FormArray<MedlemImporterSkjemalinjeType>([]),
        deaktiverte: new FormArray<MedlemImporterSkjemalinjeType>([])
    });
    tilgjengeligeGrupper = signal<GruppeModell[]>([]);

    visDeaktiverte = signal(false);
    antallValgt = signal(0);
    alleValgt = computed(() => this.antallValgt() == this.initieltAntallValgt());
    ingenValgt = computed(() => this.antallValgt() == 0);

    /*private*/ initieltAntallValgt = signal(0);

    constructor(private avhengigheter: MedlemImporterFraGruppeKomponentAvhengigheter,
                private gjeldendeTidssone: GjeldendeTidssone,
                private destroyRef: DestroyRef) {
        this.valgtGruppe.valueChanges
          .pipe(takeUntilDestroyed())
          .subscribe(valgtGruppe => this.setValgtGruppe(valgtGruppe!));
    }

    endreAlle() {
        const erValgt = !this.alleValgt();
        [...this.skjema.controls.medlemmer.controls, ...this.skjema.controls.invitasjoner.controls]
          .forEach(kontroll => MedlemImporterSkjemalinjeKomponent.setSkjemalinjeValgt(kontroll, erValgt, true));
    }

    //MedlemImporterSkjema implementasjon

    somSpesifikasjon(): GruppeInvitasjonSpesifikasjoner {
        const invitasjoner = [
            ...this.skjema.value.medlemmer!,
            ...this.skjema.value.invitasjoner!,
            ...this.skjema.value.deaktiverte!]
          .filter(linje => linje.valgt)
          .map(linje => this.linjeSomSpesifikasjon(linje));

        return {
            gruppeId: this.gruppeId(),
            invitasjoner: invitasjoner
        };
    }

    ngOnInit(): void {
        //kan ikke gjøres i konstruktør da gruppeId da enda ikke er klar for å levere verdier
        this.avhengigheter.tilgjengeligeGrupper()
          .pipe(takeUntilDestroyed(this.destroyRef), this.alleGrupperUnntattDenne())
          .subscribe(grupper => this.tilgjengeligeGrupper.set(grupper));
    }

    //private hjelpemetoder

    private alleGrupperUnntattDenne():OperatorFunction<GruppeModell[], GruppeModell[]> {
        return map(grupper => grupper.filter(gruppe => gruppe.id != this.gruppeId()));
    }

    private resetSkjema() {
        this.skjema.controls.medlemmer.clear();
        this.skjema.controls.invitasjoner.clear();
        this.skjema.controls.deaktiverte.clear();
        this.initieltAntallValgt.set(0);
        this.antallValgt.set(0);
    }

    private linjeSomSpesifikasjon(linje: MedlemImporterSkjemalinjeVerdier): OpprettGruppeInvitasjonSpesifikasjon {
        return MedlemImporterSkjemalinjeKomponent.somSpesifikasjon(linje, this.gruppeId(), this.gjeldendeTidssone.gruppensTidssone(this.gruppeId()))
    }

    private setValgtGruppe(valgtGruppe: GruppeModell) {
        debug('gruppe valgt', valgtGruppe)
        this.resetSkjema();
        medlemmerMedTilgangMedBrukertilknytning(valgtGruppe.alleMedlemmer)
          .map(medlem => this.nySkjemalinje(medlem, true))
          .forEach(linje => this.skjema.controls.medlemmer.push(linje));

        medlemmerMedTilgangUtenBrukertilknytning(valgtGruppe.alleMedlemmer)
          .map(medlem => this.nySkjemalinje(medlem, true))
          .forEach(linje => this.skjema.controls.invitasjoner.push(linje));

        medlemmerUtenTilgangMedBrukertilknytning(valgtGruppe.alleMedlemmer)
          .map(medlem => this.nySkjemalinje(medlem, false))
          .forEach(linje => this.skjema.controls.deaktiverte.push(linje));

        this.initieltAntallValgt.set(this.skjema.value.medlemmer!.length + this.skjema.value.invitasjoner!.length);
    }

    private nySkjemalinje(medlem: GruppeMedlemListeModell, initieltValgt: boolean): MedlemImporterSkjemalinjeType {
        const linjeverdier = {
            valgt: initieltValgt,
            navn: medlem.navn,
            epost: medlem.epost,
            mobilnummer: trimTilNull(medlem.mobilnummer),
            tilgangsroller: medlem.tilgangsroller,
            oppdaterMoetemal: false,
            skalSignereSomStandard: false,
            ...this.finnMedlemsrolle(primærMedlemsrolle(medlem))
        };
        const skjemalinje = MedlemImporterSkjemalinjeKomponent.opprettSkjema(this.destroyRef);
        skjemalinje.setValue(linjeverdier)

        if (initieltValgt) this.antallValgt.update(antall => antall + 1);
        else MedlemImporterSkjemalinjeKomponent.setSkjemalinjeValgt(skjemalinje, false)

        //hold antallValgt-teller i synk
        skjemalinje.controls.valgt.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(valgt =>
            this.antallValgt.update(antall => antall + (valgt ? 1 : -1)));

        return skjemalinje;
    }

    private finnMedlemsrolle(forespurtMedlemsrolle: MedlemsRollePeriode2Modell | null): {
        rolle: MedlemsRolleModell | null;
        forespurtRolle: string | null;
        rolleStartdato: PlainDate | null;
    } {
        const rolle = this.medlemsroller().find(rolle => erLikIgnorerCasing(rolle.tittel, forespurtMedlemsrolle?.tittel)) || null;
        const rolleStartdato = erIkkeNil(rolle) ? lesPlainDateSerialisert(forespurtMedlemsrolle!.periode.start) : null;
        const forespurtRolle = erNil(rolle) ? forespurtMedlemsrolle?.tittel || null : null;
        return {rolle, forespurtRolle, rolleStartdato};
    }
}


@Injectable()
export class MedlemImporterFraGruppeKomponentAvhengigheter {

    constructor(private gruppeDatatjeneste: GruppeDatatjeneste) {
    }

    /** Returnerer gruppene bruker kan kopiere medlemmer fra */
    tilgjengeligeGrupper(): Observable<GruppeModell[]> {
        return this.gruppeDatatjeneste.tilgjengeligeGrupper();
    }
}
